import { Auth0Provider, Auth0ProviderOptions } from "@auth0/auth0-react";
import { ThemeProvider } from "next-themes";

import { getConfig } from "./components/config";

import { usePathname, useRouter } from "@/lib/navigation";
import { useEffect } from "react";
import { ToastContainer } from "react-toastify";
import Popout from "./pages/Popout";
import Home from "./pages/index";
import NotFound from "./pages/NotFound";

import useTableUIStore from "@/store/tableUIStore";
import useNotificationPreferencesStore from "@/store/notifications/notificationPreferencesStore";
import useNotificationStore from "@/store/notificationStore";
// WYWA: disabled for v1
// import useBlotterSnapshotStore from "@/store/notifications/blotterSnapshotStore";
import tradeDataStore from "@/store/tradeDataStore";
import featureFlagStore from "@/store/featureFlagStore";
import { startClock, stopClock } from "@/store/clockStore";
import {
  startExpiryReconcileSweep,
  stopExpiryReconcileSweep,
} from "@/store/tradeExpiryACTIONS";
import { notifyUser } from "@/lib/browserNotifications";
import { SessionChecker } from "@/components/SessionChecker";
import { FeatureFlagLoader } from "@/components/FeatureFlagLoader";
import ErrorBoundary from "@/components/ErrorBoundary";
import { ErrorLogTokenRegistrar } from "@/components/ErrorLogTokenRegistrar";

function App() {
  const initializeState = useTableUIStore((state) => state.initializeState);
  const initializeNotificationPreferences = useNotificationPreferencesStore(
    (state) => state.initializeState,
  );
  const fontSize = useTableUIStore((state) => state.fontSize);
  useEffect(() => {
    initializeState();
    initializeNotificationPreferences();
  }, [initializeState, initializeNotificationPreferences]);

  // Initialize notification persistence and blotter snapshots once trade data has loaded
  const tradeDataHasReturned = tradeDataStore(
    (state) => state.tradeDataHasReturned,
  );

  useEffect(() => {
    if (!tradeDataHasReturned) return;

    // Load persisted notifications, filtering against current blotter
    useNotificationStore.getState().initializeFromLocalStorage();

    // WYWA: disabled for v1
    // useBlotterSnapshotStore.getState().computeAndStoreDiff();
  }, [tradeDataHasReturned]);

  // WYWA: disabled for v1
  // useEffect(() => {
  //   if (!tradeDataHasReturned) return;
  //
  //   const SNAPSHOT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
  //   const intervalId = setInterval(() => {
  //     useBlotterSnapshotStore.getState().saveSnapshot();
  //   }, SNAPSHOT_INTERVAL_MS);
  //
  //   return () => clearInterval(intervalId);
  // }, [tradeDataHasReturned]);

  // LQC-2173 - the single app-wide 1s tick behind every expiry countdown, plus
  // the sweep that reports lapsed trades the backend hasn't caught. App level,
  // not per widget, so the sweep still runs when the column is scrolled out of
  // view (AG Grid only mounts renderers for visible rows).
  //
  // The flag is a dependency, not a one-shot check: turning the feature off
  // must stop the timer, and if the flag ever becomes backend-driven it will
  // arrive AFTER mount, so a check that only ran once would never start.
  const tradeExpiryCountdown = featureFlagStore(
    (state) => state.tradeExpiryCountdown,
  );
  useEffect(() => {
    if (!tradeExpiryCountdown) return;
    startClock();
    startExpiryReconcileSweep();
    return () => {
      stopExpiryReconcileSweep();
      stopClock();
    };
  }, [tradeExpiryCountdown]);

  // Map font size to Tailwind classes for global application
  const globalFontSizeClass = {
    small: "text-xs",
    medium: "text-sm",
    large: "text-base",
    "x-large": "text-lg",
  }[fontSize];

  //If permissions have not been set by user, request notification permissions
  useEffect(() => {
    if (Notification.permission === "default") {
      notifyUser();
    }
  }, []);

  const router = useRouter();
  const pathName = usePathname();

  const onRedirectCallback = (appState: any) => {
    const url =
      appState && appState.returnTo
        ? appState.returnTo
        : window.location.pathname;
    router.push(url);
  };

  // Please see https://auth0.github.io/auth0-react/interfaces/Auth0ProviderOptions.html
  // for a full list of the available properties on the provider
  const config = getConfig();

  const providerConfig: Auth0ProviderOptions = {
    domain: config.domain,
    clientId: config.clientId,
    onRedirectCallback,
    cacheLocation: "localstorage",
    authorizationParams: {
      redirect_uri: typeof window !== "undefined" ? window.location.origin : "",
      ...(config.audience ? { audience: config.audience } : null),
    },
  };

  return (
    <ErrorBoundary>
    <div className={globalFontSizeClass}>
      <ThemeProvider attribute="class" defaultTheme="dark">
        <Auth0Provider {...providerConfig}>
          {/* GLOBAL SESSION CHECK */}
          <SessionChecker />

          {/* Fetch feature flags per-window so the pop-out (separate JS context) gets them too */}
          <FeatureFlagLoader />

          {/* Hands the auth token accessor to the root ErrorBoundary's logger */}
          <ErrorLogTokenRegistrar />

          {pathName.includes("/Popout") ? (
            <div className="h-full w-full">
              <Popout />
              <ToastContainer
                position="bottom-right"
                autoClose={2000}
                newestOnTop
                closeOnClick={true}
                hideProgressBar={true}
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="dark"
              />
            </div>
          ) : pathName === "/" ? (
            <div className="flex h-screen w-full flex-col">
              <div id="modal"></div>

              <div className="h-[calc(100vh)] w-full">
                <ToastContainer
                  position="bottom-right"
                  autoClose={2000}
                  newestOnTop
                  closeOnClick={true}
                  hideProgressBar={true}
                  rtl={false}
                  pauseOnFocusLoss
                  draggable
                  pauseOnHover
                  theme="dark"
                />
                <Home />
              </div>
            </div>
          ) : (
            <NotFound />
          )}
        </Auth0Provider>
      </ThemeProvider>
    </div>
    </ErrorBoundary>
  );
}

export default App;
